// ============================================================================
// CrewAhead Maritime Platform - Employer API Functions
// ============================================================================
// Aligned with backend: CompanyProfileSerializer + CompanyProfileUpdateSerializer
// All FKs are raw integer PKs (not nested objects). Display names are read-only.
// The API client auto-converts snake_case → camelCase.

import { apiClient, handleApiError } from './client';
import type { CompanyProfile } from '@/lib/types/models';

// ─── Input Types ─────────────────────────────────────────────────────────────

export interface CompanyProfileInput {
  companyName?: string;
  tradingName?: string;
  companyTypeId?: number | null;
  companySize?: string;
  registrationNumber?: string;
  taxId?: string;
  yearEstablished?: number | null;
  website?: string;
  email?: string;
  phone?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  country?: string;
  postalCode?: string;
  fleetSize?: number | null;
  vesselTypeIds?: number[];
  flagStates?: string[];
  tradingRouteIds?: number[];
  description?: string;
  missionStatement?: string;
  isActive?: boolean;
  rpslNumber?: string;
  hiringVesselCategoryIds?: number[];
  hiringRankIds?: number[];
  hiringShorePositionIds?: number[];
  hiringDepartmentIds?: number[];
  preferredContractTypeIds?: number[];
  typicalContractDurationId?: number | null;
  immediateHiringNeeds?: boolean;
  hiringNotes?: string;
}

export interface CompanyDocument {
  id: number;
  documentType: string;
  title: string;
  documentFile: string | null;
  expiryDate: string | null;
  isVerified: boolean;
  createdAt: string;
}

export type EmployerTeamRole = "ADMIN" | "CREWING_MANAGER";

export interface EmployerTeamMember {
  id: number;
  userId: number;
  userEmail: string;
  userName: string;
  role: EmployerTeamRole;
  roleDisplay: string;
  canPostJobs: boolean;
  canViewCvs: boolean;
  canShortlist: boolean;
  canContact: boolean;
  joinedAt: string;
}

export interface UpdateTeamMemberRoleInput {
  role: EmployerTeamRole;
}

export interface TransferOwnershipInput {
  newOwnerUserId: number;
}

export interface TransferOwnershipResponse {
  id: number;
  ownerName: string;
  ownerEmail: string;
}

export interface InviteTeamMemberInput {
  email: string;
  firstName?: string;
  lastName?: string;
  role: EmployerTeamRole;
}


export interface EmployerDashboardData {
  companyName: string;
  companyType: string;
  fleetSize: number;
  isVerified: boolean;
  activeJobsCount: number;
  totalApplications: number;
  shortlistedCount: number;
  teamSize: number;
  subscriptionTier: string | null;
  jobPostsRemaining: number | null;
  cvViewsRemaining: number | null;
  pendingDocuments: number;
  newApplicationsThisWeek: number;
}

export interface SubscriptionData {
  id?: number;
  tier?: string;
  status?: string;
  maxJobPosts?: number;
  maxCvViews?: number;
  maxUsers?: number;
  jobPostsUsed?: number;
  cvViewsUsed?: number;
  jobPostsRemaining?: number;
  cvViewsRemaining?: number;
  amountPaid?: string;
  currency?: string;
  startedAt?: string;
  expiresAt?: string | null;
  isActive?: boolean;
  // Fallback fields
  plan?: string;
  detail?: string;
}

// ─── My Profile (self-resolving, one-pass) ───────────────────────────────────

/**
 * GET/PATCH /employers/company/my-profile/
 *
 * Self-resolving endpoint — finds the company profile for the current user
 * (as owner OR team member). Returns blockers + can_post_jobs.
 *
 * This is the PRIMARY endpoint for employer profile view/edit.
 */
export async function getMyEmployerProfile(): Promise<CompanyProfile> {
  const response = await apiClient.get<CompanyProfile>(
    '/employers/company/my-profile'
  );
  return response.data;
}

export async function updateMyEmployerProfile(
  data: CompanyProfileInput
): Promise<CompanyProfile> {
  const response = await apiClient.patch<CompanyProfile>(
    '/employers/company/my-profile',
    data
  );
  return response.data;
}

// ─── Company Profile CRUD (admin or multi-company use) ──────────────────────

export async function getCompanyProfiles(): Promise<{
  count: number;
  results: CompanyProfile[];
}> {
  try {
    const response = await apiClient.get<{
      count: number;
      results: CompanyProfile[];
    }>('/employers/company');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function createCompanyProfile(
  data: CompanyProfileInput
): Promise<CompanyProfile> {
  try {
    const response = await apiClient.post<CompanyProfile>(
      '/employers/company',
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /employers/company/my-profile/logo/presigned
 *
 * Request a presigned S3 POST URL for uploading a company logo.
 */
export async function getCompanyLogoPresignedUrl(
  file: File
): Promise<{ url: string; fields: Record<string, string>; key: string }> {
  const response = await apiClient.get<{
    url: string;
    fields: Record<string, string>;
    key: string;
  }>(
    `/employers/company/my-profile/logo/presigned?filename=${encodeURIComponent(
      file.name
    )}&content_type=${encodeURIComponent(file.type)}&file_size=${file.size}`
  );
  return response.data;
}

/**
 * POST /employers/company/my-profile/logo/confirm
 *
 * Confirm a logo upload after it has been posted to S3.
 */
export async function confirmCompanyLogoUpload(
  key: string,
  file: File
): Promise<CompanyProfile> {
  const response = await apiClient.post<CompanyProfile>(
    '/employers/company/my-profile/logo/confirm',
    {
      key,
      file_name: file.name,
      file_size: file.size,
      mime_type: file.type,
    }
  );
  return response.data;
}

/**
 * POST the logo file directly to S3 using a presigned URL.
 */
export async function uploadLogoToS3(
  presigned: { url: string; fields: Record<string, string> },
  file: File
): Promise<void> {
  const formData = new FormData();
  Object.entries(presigned.fields).forEach(([k, v]) => {
    formData.append(k, v);
  });
  formData.append('file', file);

  const response = await fetch(presigned.url, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`S3 upload failed: ${response.status} ${text}`);
  }
}

/**
 * Upload a company logo using the presigned S3 flow.
 */
export async function uploadCompanyLogo(file: File): Promise<CompanyProfile> {
  const presigned = await getCompanyLogoPresignedUrl(file);
  await uploadLogoToS3(presigned, file);
  return confirmCompanyLogoUpload(presigned.key, file);
}

/**
 * DELETE /employers/company/my-profile/logo
 *
 * Remove the company logo.
 */
export async function removeCompanyLogo(): Promise<CompanyProfile> {
  const response = await apiClient.delete<CompanyProfile>(
    '/employers/company/my-profile/logo'
  );
  return response.data;
}

// ─── Documents ───────────────────────────────────────────────────────────────

export async function getCompanyDocuments(
  companyId: number
): Promise<{ count: number; results: CompanyDocument[] }> {
  const response = await apiClient.get<{
    count: number;
    results: CompanyDocument[];
  }>(`/employers/company/${companyId}/documents`);
  return response.data;
}

/**
 * Upload a company document with actual file (multipart/form-data).
 * Backend expects: document_file (File), document_type, title, expiry_date
 */
export async function uploadCompanyDocument(
  companyId: number,
  formData: FormData
): Promise<CompanyDocument> {
  const response = await apiClient.post<CompanyDocument>(
    `/employers/company/${companyId}/documents`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
}

// ─── Team ────────────────────────────────────────────────────────────────────

export async function getCompanyTeamMembers(
  companyId: number
): Promise<{ count: number; results: EmployerTeamMember[] }> {
  const response = await apiClient.get<{
    count: number;
    results: EmployerTeamMember[];
  }>(`/employers/company/${companyId}/team`);
  return response.data;
}

export async function inviteTeamMember(
  companyId: number,
  data: InviteTeamMemberInput
): Promise<EmployerTeamMember> {
  const response = await apiClient.post<EmployerTeamMember>(
    `/employers/company/${companyId}/team`,
    data
  );
  return response.data;
}

export async function updateTeamMemberRole(
  companyId: number,
  memberId: number,
  data: UpdateTeamMemberRoleInput
): Promise<EmployerTeamMember> {
  const response = await apiClient.patch<EmployerTeamMember>(
    `/employers/company/${companyId}/team/${memberId}`,
    data
  );
  return response.data;
}

export async function removeTeamMember(
  companyId: number,
  memberId: number
): Promise<void> {
  await apiClient.delete(`/employers/company/${companyId}/team/${memberId}`);
}

export async function transferCompanyOwnership(
  companyId: number,
  data: TransferOwnershipInput
): Promise<TransferOwnershipResponse> {
  const response = await apiClient.post<TransferOwnershipResponse>(
    `/employers/company/${companyId}/transfer-ownership`,
    data
  );
  return response.data;
}

// ─── Dashboard ───────────────────────────────────────────────────────────────


export async function getEmployerDashboard(): Promise<EmployerDashboardData> {
  const response = await apiClient.get<EmployerDashboardData>(
    '/employers/dashboard'
  );
  return response.data;
}

// ─── Subscription ────────────────────────────────────────────────────────────

export async function getEmployerSubscription(): Promise<SubscriptionData> {
  const response = await apiClient.get<SubscriptionData>(
    '/employers/subscription'
  );
  return response.data;
}
